import React, {useEffect, useRef} from "react";
import EasasPageTemplateWrapper from "../../../templates/EasasPageTemplateWrapper";
import {
    useGetActionJobInfo,
    useGetActionJobTimes,
    useGetMigrationActions,
    useGetMigrationTypes,
    useRunMigrationActionMutation
} from "api/api-hooks/migrations-hooks";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {TabPanel, TabView} from "primereact/tabview";
import {Button} from "primereact/button";

import {MigrationActionInfo} from "api/model/migrations/types";
import {JodaTimeUtils} from "utils/joda-time-utils";
import {DEFAULT_DATE_TIME_PATTERN} from "utils/constants";
import formatInstantAsLocal = JodaTimeUtils.formatInstantAsLocal;

const MigrationPage: React.FC = () => {
    const [currentType, setCurrentType] = React.useState<string | undefined>(undefined);
    const [currentAction, setCurrentAction] = React.useState<MigrationActionInfo | undefined>(undefined);
    const [currentTimestamp, setCurrentTimestamp] = React.useState<string | undefined>();
    const [logRefresh, setLogRefresh] = React.useState<boolean>(false);

    const [actionData, setActionData] = React.useState<Record<string, string | null>>({});
    const [activeTab, setActiveTab] = React.useState<number>(0);

    const logsEndRef = useRef<null | HTMLDivElement>(null)

    const migTypes = useGetMigrationTypes()
    const actionTypes = useGetMigrationActions(currentType!, {enabled: !!currentType});
    const jobInfo = useGetActionJobInfo(currentType!, currentAction?.action!, currentTimestamp!,
        {
            enabled: !!currentType && !!currentAction && !!currentTimestamp,
            refetchInterval: logRefresh ? 1000 : false,
        }
    );

    const jobTimes = useGetActionJobTimes(currentType!, currentAction?.action!, {
        enabled: !!currentType && !!currentAction,
    });

    const runMutation = useRunMigrationActionMutation({
        onSuccess: (data: string) => {
            setCurrentTimestamp(data);
            setActiveTab(1);
    }});

    // Auto select action when type is selected
    useEffect(() => {
        if (actionTypes.isSuccess) {
            setCurrentAction(actionTypes?.data[0]);
        }
    }, [actionTypes.isSuccess, actionTypes.data]);

    // turn on auto refresh when current job is RUNNING
    useEffect(() => {
        if (jobInfo.data) {
            setLogRefresh(jobInfo.data.status === "RUNNING");
        }
    }, [jobInfo?.data?.status]);


    // Handle auto select of timestamp when action or type changes
    useEffect(() => {
        if (jobTimes.data?.length) {
            setCurrentTimestamp(jobTimes.data[0]);
        }
    }, [currentAction, currentType, jobTimes.isLoading]);

    useEffect(() => {
        if(activeTab === 1)
        scrollToBottom()
    }, [jobInfo.data]);

    const timestampSelections = jobTimes.data
        ?.map( t => { return { time: t, label: formatInstantAsLocal(t, DEFAULT_DATE_TIME_PATTERN) };
    })

    const scrollToBottom = () => {
        logsEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }


    const onActionDataChange = (param: string, value: string | null) => {
        setActionData(prev => ({
            ...prev,
            [param]: value
        }));
    }

    const onTypeChange = (e: { value: string }) => {
        setCurrentType(e.value);
        setCurrentAction(undefined);
        setCurrentTimestamp(undefined);
        setActionData({});
    }

    const onActionChange = (e: { value: MigrationActionInfo }) => {
        setCurrentAction(e.value);
        setActionData({});
        setCurrentTimestamp(undefined);
    }

    const onTimestampChange = (e: { value: string }) => {
        setCurrentTimestamp(e.value);
    }

    const onActionRun = (e: HTMLFormElement) => {
        e.preventDefault()
        console.log("Running action with data:", actionData);

        runMutation.mutate({
            migration: currentType!,
            action: currentAction!.action,
            data: actionData
        });

        setActionData({});
    }

    return <EasasPageTemplateWrapper title="Migrations">
        <div className="flex flexColumn gap-2">
            <h5>Migration actions</h5>

            <Dropdown value={currentType}
                      onChange={onTypeChange}
                      options={migTypes.data}
                      placeholder="Select migration"
                      className="w-full"
            />

            { actionTypes.data && (
                <Dropdown value={currentAction}
                          onChange={onActionChange}
                          options={actionTypes.data}
                          optionLabel="action"
                          placeholder="Select action"
                          className="w-full"
                />
            )}
            <TabView activeIndex={activeTab} onTabChange={e => setActiveTab(e.index)}>
                <TabPanel header="Migrations" key="migrations">
                    <div className="flex flexColumn gap-2">
                        <form id={currentAction?.action} name={currentAction?.action} onSubmit={onActionRun} >
                          { !!currentAction?.parameters?.length && (
                            <div>
                                <h6>Action parameters</h6>

                                { currentAction.parameters.map( (param) => (
                                    <div className="flex flex-column gap-2" key={param}>
                                        <label>{param}</label>
                                        <InputText
                                            id={param}
                                            name={param}
                                            value={actionData[param] ?? ''}
                                            onChange={e => onActionDataChange(param, e.target.value)}
                                            placeholder={`Enter value for ${param}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        <Button
                            label="Run action"
                            disabled={!currentAction && !runMutation.isPending}
                            type="submit"
                        />
                        </form>
                    </div>
                </TabPanel>

                <TabPanel header="Status/Logs" key="status">
                    <div className="flex flexColumn gap-2">
                        <div>
                            { currentAction && (
                                <Dropdown value={currentTimestamp}
                                          onChange={onTimestampChange}
                                          optionValue="time"
                                          options={timestampSelections}
                                          placeholder="Select Timestamp"
                                          className="w-full"
                                />
                            )}
                        </div>

                        <div>
                            <label style={{fontWeight: "bold"}}>Status:</label>
                            <span>{jobInfo?.data?.status}</span>
                        </div>

                        <div>
                            <label style={{fontWeight: "bold"}}>Started:</label>
                            <span>{formatInstantAsLocal(jobInfo?.data?.started, DEFAULT_DATE_TIME_PATTERN)}</span>
                        </div>

                        <div>
                            <label style={{fontWeight: "bold"}}>Finished:</label>
                            <span>{formatInstantAsLocal(jobInfo?.data?.finished, DEFAULT_DATE_TIME_PATTERN)}</span>
                        </div>

                        <h6>Logs (refresh: {logRefresh ? "ON" : "OFF"})</h6>
                        <div style={{height: "500px", overflowY: "scroll"}}>
                            <table>
                                <thead style={{backgroundColor: '#d2caca'}}>
                                <tr>
                                    <th style={{width: "150px"}}>Time</th>
                                    <th style={{width: "50px"}}>Duration</th>
                                    <th>Message</th>
                                </tr>
                                </thead>
                                <tbody>
                                { jobInfo.data?.logs.map((item, index) => (
                                    <tr key={index}>
                                        <td>{formatInstantAsLocal(item.timestamp, DEFAULT_DATE_TIME_PATTERN)}</td>
                                        <td>{item.duration}</td>
                                        <td style={{whiteSpace: "pre-wrap"}}>{item.message}</td>
                                    </tr>
                                )) }
                                </tbody>
                            </table>

                            <div ref={logsEndRef} />
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </div>
    </EasasPageTemplateWrapper>
}

export default MigrationPage;